'use client';

import { Skeleton, SkeletonText, SkeletonCircle } from '@/components/ui/skeleton';

// ─── Surface Skeletons ──────────────────────────────────────────────────────
// Each skeleton matches the final layout of its surface to avoid CLS.
// Subtle pulse, no heavy shimmer. Low-cost, lightweight.

// ─── App Shell Skeleton ─────────────────────────────────────────────────────
// Full page skeleton for initial app load (sidebar + header + main content).

export function AppShellSkeleton() {
  return (
    <div className="flex h-dvh w-full overflow-hidden">
      {/* Sidebar — hidden on tablet, fixed width on desktop */}
      <aside className="border-border/30 bg-sidebar hidden h-full w-[240px] shrink-0 flex-col border-r lg:flex">
        {/* Header */}
        <div className="border-border/20 flex h-[56px] shrink-0 items-center gap-2.5 border-b px-3.5 py-3">
          <Skeleton className="h-7 w-7 shrink-0 rounded-lg" />
          <Skeleton className="h-4 w-24" />
        </div>
        {/* Workspace switcher */}
        <div className="shrink-0 space-y-1.5 px-3 py-2">
          <Skeleton className="h-9 w-full rounded-lg" />
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>
        <div className="bg-border/20 mx-3 mb-1 h-px shrink-0" />
        {/* Nav groups */}
        <div className="min-h-0 flex-1 overflow-hidden px-3 pt-1">
          <div className="space-y-4">
            {[4, 3, 3, 2].map((count, gi) => (
              <div key={gi} className="space-y-1">
                <Skeleton className="mb-1.5 h-3 w-16" />
                {Array.from({ length: count }).map((_, i) => (
                  <Skeleton key={i} className="h-7 w-full rounded-lg" />
                ))}
              </div>
            ))}
          </div>
        </div>
        {/* Footer */}
        <div className="border-border/20 shrink-0 space-y-2 border-t px-3 py-3">
          <Skeleton className="h-8 w-full rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-7 flex-1 rounded-lg" />
            <Skeleton className="h-7 w-7 shrink-0 rounded-lg" />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="border-border/30 flex h-11 shrink-0 items-center justify-between border-b px-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <Skeleton className="h-3.5 w-20" />
            <span className="text-muted-foreground/10">/</span>
            <Skeleton className="h-3.5 w-16" />
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Skeleton className="hidden h-7 w-24 rounded-md sm:block" />
            <div className="bg-border/20 mx-1.5 hidden h-3.5 w-px sm:block" />
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-7 w-7 rounded-md" />
            ))}
          </div>
        </div>

        {/* Content area */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <Skeleton className="h-6 w-48" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

// ─── Header Skeleton ────────────────────────────────────────────────────────

export function HeaderSkeleton() {
  return (
    <div className="border-border/30 bg-background/90 flex h-11 shrink-0 items-center justify-between border-b px-3">
      <div className="flex min-w-0 items-center gap-1.5">
        <Skeleton className="h-3.5 w-20 shrink-0" />
        <span className="text-muted-foreground/10 shrink-0">/</span>
        <Skeleton className="h-3.5 w-16 shrink-0" />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Skeleton className="hidden h-7 w-24 rounded-md sm:block" />
        <div className="bg-border/20 mx-1.5 hidden h-3.5 w-px sm:block" />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-7 w-7 shrink-0 rounded-md" />
        ))}
      </div>
    </div>
  );
}

// ─── Home Board Skeleton ────────────────────────────────────────────────────
// Mirrors PageContainer variant="default" (max-w clamp 600/75vw/920) + the
// HomeBoard zone stack: identity hero, quick actions grid, stats grid,
// and two surface card rows.

export function HomeBoardSkeleton() {
  return (
    <main
      className="animate-fade-in h-full w-full overflow-x-hidden overflow-y-auto"
      role="main"
      id="main-content"
      aria-label="Home"
      aria-busy="true"
    >
      <div
        className="mx-auto flex min-h-full w-full max-w-[clamp(600px,75vw,920px)] flex-col space-y-10 px-4 py-10 md:px-6 lg:px-8 lg:py-12"
        role="status"
        aria-live="polite"
        aria-label="Loading workspace home"
      >
        {/* Identity hero */}
        <div className="space-y-3">
          <Skeleton className="h-3 w-32 rounded" />
          <Skeleton className="h-8 w-64 rounded-md" />
          <Skeleton className="h-4 w-80 max-w-full rounded" />
        </div>

        {/* Quick actions — 3 cols on lg */}
        <section className="space-y-3">
          <Skeleton className="h-3 w-24 rounded" />
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="border-border/50 bg-card flex items-center gap-3 rounded-xl border p-3.5 shadow-sm shadow-black/[0.02]"
              >
                <Skeleton className="size-9 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4 max-w-[160px] rounded" />
                  <Skeleton className="h-3 w-2/3 max-w-[140px] rounded" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Stats overview — 4 cols on sm */}
        <section className="space-y-3">
          <Skeleton className="h-3 w-20 rounded" />
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="border-border/50 bg-card rounded-xl border p-4 shadow-sm shadow-black/[0.02]"
              >
                <Skeleton className="size-5 rounded" />
                <Skeleton className="mt-3 h-6 w-12 rounded" />
                <Skeleton className="mt-1.5 h-3 w-16 rounded" />
              </div>
            ))}
          </div>
        </section>

        {/* Surface cards (insights / recent activity) */}
        <section className="space-y-3">
          <Skeleton className="h-3 w-28 rounded" />
          <div className="space-y-2.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="border-border/40 bg-card/40 flex items-center gap-3 rounded-xl border p-3.5"
              >
                <Skeleton className="size-9 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4 max-w-md rounded" />
                  <Skeleton className="h-3 w-1/2 max-w-xs rounded" />
                </div>
                <Skeleton className="hidden h-3 w-16 shrink-0 rounded sm:block" />
              </div>
            ))}
          </div>
        </section>
      </div>
      <span className="sr-only">Loading workspace home</span>
    </main>
  );
}

// ─── Home Board Surfaces Skeleton ───────────────────────────────────────────
// In-component skeleton for Zone B (server-ordered affordances) on the home
// page. Renders while `homeLoading && !home` — chrome (identity hero, quick
// actions, stats) is already on screen, but the operational rail is empty.
// The skeleton makes no claim about which specific surface kinds will render
// (the server decides) — it's two generic "section card" blocks that hold
// geometry while the response is in flight. When real surfaces arrive they
// stagger-fade in over the skeleton — no layout shift, no abrupt snap.

export function HomeBoardSurfacesSkeleton() {
  return (
    <div
      className="animate-in fade-in space-y-4 duration-200 motion-reduce:animate-none"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading workspace surfaces"
      data-testid="home-surfaces-skeleton"
    >
      {Array.from({ length: 2 }).map((_, cardIdx) => (
        <div key={cardIdx} className="border-border/40 bg-card/40 space-y-3 rounded-xl border p-4">
          {/* Section header bar */}
          <div className="flex items-center justify-between">
            <Skeleton className="h-3.5 w-32 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
          </div>
          {/* 3 muted content rows */}
          <div className="space-y-2.5">
            {Array.from({ length: 3 }).map((_, rowIdx) => (
              <div key={rowIdx} className="flex items-center gap-3">
                <Skeleton className="size-8 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4 max-w-md rounded" />
                  <Skeleton className="h-3 w-1/2 max-w-xs rounded" />
                </div>
                <Skeleton className="hidden h-3 w-14 shrink-0 rounded sm:block" />
              </div>
            ))}
          </div>
        </div>
      ))}
      <span className="sr-only">Loading workspace surfaces</span>
    </div>
  );
}

// ─── Graph Skeleton ─────────────────────────────────────────────────────────
// Mirrors KnowledgeGraph (src/components/graph/knowledge-graph.tsx): a thin
// toolbar row ("N papers · M connections" on the left, edge-filter + zoom +
// view-mode buttons on the right) and a large canvas surface beneath it.
// Sits inside PageContainer variant="full" (px-0, py-10), so this skeleton
// supplies its own vertical breathing room and a full-bleed horizontal box.
// PlanGate now renders null while the plan resolves, so this route-boundary
// skeleton is the only thing the user sees during the initial /graph load.

export function GraphSkeleton() {
  return (
    <main
      className="animate-fade-in h-full w-full overflow-hidden"
      role="main"
      id="main-content"
      aria-label="Graph"
      aria-busy="true"
    >
      <div
        className="mx-auto flex h-full w-full flex-col gap-3 py-10"
        role="status"
        aria-live="polite"
        aria-label="Loading citation graph"
      >
        {/* Toolbar */}
        <div className="flex shrink-0 items-center justify-between px-4 md:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <Skeleton className="size-3.5 shrink-0 rounded" />
            <Skeleton className="h-3 w-32 rounded" />
          </div>
          <div className="flex items-center gap-1">
            <Skeleton className="hidden h-8 w-24 rounded-md md:block" />
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="size-8 shrink-0 rounded-md" />
            ))}
            <div className="bg-border/30 mx-0.5 hidden h-4 w-px md:block" />
            <Skeleton className="size-8 shrink-0 rounded-md" />
            <Skeleton className="size-8 shrink-0 rounded-md" />
          </div>
        </div>

        {/* Canvas placeholder */}
        <div className="min-h-0 flex-1 px-4 md:px-6 lg:px-8">
          <Skeleton className="h-full min-h-[400px] w-full rounded-xl" />
        </div>
      </div>
      <span className="sr-only">Loading citation graph</span>
    </main>
  );
}

// ─── Trash Skeleton ─────────────────────────────────────────────────────────
// Mirrors TrashView: p-6 stack with header, retention warning banner,
// tab strip, and 4 list rows. Used by both the route boundary and
// TrashView's client-side query loading state.

export function TrashSkeleton() {
  return (
    <div
      className="animate-fade-in flex flex-col gap-6 p-6"
      role="status"
      aria-label="Loading trash"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="size-5 rounded" />
            <Skeleton className="h-6 w-20 rounded" />
          </div>
          <Skeleton className="h-4 w-32 rounded" />
        </div>
      </div>

      {/* Warning banner */}
      <Skeleton className="h-16 w-full rounded-lg" />

      {/* Tab strip + list rows */}
      <div className="space-y-4">
        <div className="flex gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-20 rounded-md" />
        </div>
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="border-border/40 bg-background flex items-center gap-3 rounded-md border px-4 py-3"
            >
              <Skeleton className="size-4 shrink-0 rounded" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-[55%] max-w-sm rounded" />
                <Skeleton className="h-3 w-[35%] max-w-[200px] rounded" />
              </div>
              <Skeleton className="hidden h-3 w-20 shrink-0 rounded sm:block" />
            </div>
          ))}
        </div>
      </div>
      <span className="sr-only">Loading trash</span>
    </div>
  );
}

// ─── Collections Skeleton ───────────────────────────────────────────────────
// Mirrors CollectionsView: persistent sidebar (CollectionTree) on the left
// plus a main content surface with header + search row + list of rows.

export function CollectionsSkeleton() {
  return (
    <div
      className="animate-fade-in flex h-full overflow-hidden"
      role="status"
      aria-label="Loading collections"
    >
      {/* Sidebar — hidden on narrow viewports */}
      <aside className="border-border/20 hidden w-[280px] shrink-0 flex-col overflow-hidden border-r md:flex">
        <div className="border-border/20 flex shrink-0 items-center justify-between border-b px-3 py-3">
          <Skeleton className="h-4 w-24 rounded" />
          <Skeleton className="size-6 rounded-md" />
        </div>
        <div className="min-h-0 flex-1 space-y-1.5 overflow-hidden px-2 py-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5">
              <Skeleton className="size-4 shrink-0 rounded" />
              <Skeleton className="h-3.5 max-w-[140px] flex-1 rounded" />
              <Skeleton className="h-3 w-6 shrink-0 rounded" />
            </div>
          ))}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="border-border/20 border-b px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="size-9 shrink-0 rounded-lg" />
              <div className="space-y-2">
                <div className="flex items-center gap-2.5">
                  <Skeleton className="h-5 w-40 rounded" />
                  <Skeleton className="h-5 w-10 rounded-full" />
                </div>
                <Skeleton className="h-3.5 w-64 max-w-full rounded" />
              </div>
            </div>
            <Skeleton className="hidden h-3.5 w-48 shrink-0 rounded sm:block" />
          </div>
        </div>

        {/* Search */}
        <div className="border-border/10 border-b px-4 py-2.5">
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>

        {/* List */}
        <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 rounded-md px-3 py-2.5">
              <Skeleton className="size-4 shrink-0 rounded" />
              <Skeleton className="size-8 shrink-0 rounded-md" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-[60%] max-w-md rounded" />
                <Skeleton className="h-3 w-[40%] max-w-[200px] rounded" />
              </div>
              <Skeleton className="hidden h-3 w-16 shrink-0 rounded sm:block" />
            </div>
          ))}
        </div>
      </div>
      <span className="sr-only">Loading collections</span>
    </div>
  );
}

// ─── Documents Page Skeleton ────────────────────────────────────────────────
// Mirrors the real Documents route layout (header + filter bar + bordered
// table card with header row and data rows). Used by both the Next.js route
// boundary (loading.tsx) and the client-side query boundary so the user never
// sees a layout hand-off when hydration completes.

export function DocumentsPageSkeleton() {
  return (
    <main
      className="animate-fade-in flex h-full flex-col overflow-hidden"
      role="main"
      id="main-content"
      aria-label="Documents"
      aria-busy="true"
    >
      {/* Header — matches real header dimensions (border-b px-6 py-4) */}
      <div className="border-border/20 flex shrink-0 items-center justify-between border-b px-6 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <Skeleton className="size-4 shrink-0 rounded" />
          <Skeleton className="h-5 w-28 rounded" />
          <Skeleton className="h-3.5 w-14 rounded-full" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Skeleton className="hidden h-8 w-24 rounded-md md:block" />
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-32 rounded-md" />
        </div>
      </div>

      {/* Scrollable content — matches real px-6 py-5 + space-y-5 padding */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex min-h-full flex-1 flex-col space-y-5 px-6 py-5">
          {/* Filter bar */}
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-full max-w-xs rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
            <Skeleton className="hidden h-9 w-20 rounded-lg sm:block" />
            <div className="flex-1" />
            <Skeleton className="hidden h-9 w-28 rounded-lg md:block" />
          </div>

          {/* Table card — matches real DocumentTable wrapper */}
          <div className="border-border/30 bg-card/20 overflow-hidden rounded-xl border">
            {/* Header row */}
            <div className="border-border/30 bg-card/40 flex items-center gap-3 border-b px-3 py-2.5">
              <Skeleton className="size-4 shrink-0 rounded" />
              <Skeleton className="h-3 w-24" />
              <div className="flex-1" />
              <Skeleton className="hidden h-3 w-20 shrink-0 sm:block" />
              <Skeleton className="hidden h-3 w-12 shrink-0 md:block" />
              <Skeleton className="hidden h-3 w-16 shrink-0 lg:block" />
              <Skeleton className="hidden h-3 w-20 shrink-0 lg:block" />
              <div className="w-7 shrink-0" />
            </div>
            {/* Body rows */}
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="border-border/20 flex items-center gap-3 border-b px-3 py-3.5 last:border-b-0"
              >
                <Skeleton className="size-4 shrink-0 rounded" />
                <Skeleton className="size-9 shrink-0 rounded-md" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-[60%] max-w-sm rounded" />
                  <Skeleton className="h-2.5 w-[35%] max-w-[180px] rounded" />
                </div>
                <Skeleton className="hidden h-3 w-20 shrink-0 sm:block" />
                <Skeleton className="hidden h-3 w-12 shrink-0 md:block" />
                <Skeleton className="hidden h-3 w-16 shrink-0 lg:block" />
                <Skeleton className="hidden h-3 w-20 shrink-0 lg:block" />
                <Skeleton className="size-7 shrink-0 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <span className="sr-only" role="status" aria-live="polite" aria-label="Loading documents">
        Loading documents
      </span>
    </main>
  );
}

// ─── Library Preview Panel Skeleton ─────────────────────────────────────────

export function LibraryPreviewSkeleton() {
  return (
    <div className="animate-fade-in flex h-full flex-col overflow-hidden p-4">
      <div className="min-h-0 flex-1 space-y-4 overflow-hidden">
        {/* Title & meta */}
        <div className="space-y-2">
          <Skeleton className="h-5 w-[75%] max-w-[280px]" />
          <Skeleton className="h-3 w-[50%] max-w-[180px]" />
        </div>
        {/* Thumbnail */}
        <Skeleton className="h-48 w-full rounded-lg" />
        {/* Details */}
        <SkeletonText lines={4} />
        {/* Actions */}
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
      </div>
    </div>
  );
}

// ─── Document Viewer Skeleton ───────────────────────────────────────────────

export function DocumentViewerSkeleton() {
  return (
    <div className="animate-fade-in flex h-full overflow-hidden">
      {/* PDF Canvas */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Toolbar */}
        <div className="border-border/30 flex shrink-0 items-center gap-2 border-b px-3 py-2">
          <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
          <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
          <div className="flex-1" />
          <Skeleton className="h-5 w-14 shrink-0" />
          <div className="flex-1" />
          <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
          <Skeleton className="hidden h-7 w-7 shrink-0 rounded-md sm:block" />
          <Skeleton className="hidden h-7 w-7 shrink-0 rounded-md sm:block" />
        </div>
        {/* Page area */}
        <div className="flex min-h-0 flex-1 items-start justify-center overflow-hidden p-4 sm:p-6">
          <Skeleton className="h-full max-h-[600px] w-full max-w-[450px] rounded-lg" />
        </div>
      </div>

      {/* Outline / Citations sidebar — hidden on narrow viewports */}
      <div className="border-border/30 hidden w-64 shrink-0 flex-col space-y-3 overflow-y-auto border-l p-3 lg:flex">
        <Skeleton className="h-4 w-20" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-1 pl-2">
            <Skeleton className="h-3 w-[80%]" />
            <Skeleton className="h-2.5 w-[60%]" />
          </div>
        ))}
        <div className="bg-border/20 my-3 h-px" />
        <Skeleton className="h-4 w-16" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-[70%] pl-2" />
        ))}
      </div>
    </div>
  );
}

// ─── Chat Skeleton ──────────────────────────────────────────────────────────

export function ChatSkeleton() {
  return (
    <div className="animate-fade-in flex h-full flex-col overflow-hidden">
      {/* Messages area */}
      <div className="min-h-0 flex-1 space-y-4 overflow-hidden p-4">
        {/* User message */}
        <div className="flex justify-end">
          <div className="max-w-[70%] space-y-1.5">
            <Skeleton className="h-10 w-48 max-w-full rounded-2xl rounded-br-md" />
          </div>
        </div>
        {/* Assistant message */}
        <div className="flex gap-2.5">
          <SkeletonCircle size={28} className="shrink-0" />
          <div className="max-w-[70%] min-w-0 space-y-1.5">
            <Skeleton className="h-4 w-64 max-w-full" />
            <Skeleton className="h-4 w-52 max-w-full" />
            <Skeleton className="h-4 w-40 max-w-full" />
          </div>
        </div>
        {/* Source cards — scroll horizontally on narrow screens */}
        <div className="flex gap-2 overflow-x-auto pl-10">
          <Skeleton className="h-16 w-36 shrink-0 rounded-lg" />
          <Skeleton className="h-16 w-36 shrink-0 rounded-lg" />
          <Skeleton className="hidden h-16 w-36 shrink-0 rounded-lg sm:block" />
        </div>
        {/* Another exchange */}
        <div className="flex justify-end">
          <Skeleton className="h-10 w-56 max-w-full rounded-2xl rounded-br-md" />
        </div>
        <div className="flex gap-2.5">
          <SkeletonCircle size={28} className="shrink-0" />
          <div className="max-w-[70%] min-w-0 space-y-1.5">
            <Skeleton className="h-4 w-56 max-w-full" />
            <Skeleton className="h-4 w-44 max-w-full" />
          </div>
        </div>
      </div>

      {/* Input area */}
      <div className="border-border/30 shrink-0 border-t px-4 py-3">
        <Skeleton className="h-10 w-full rounded-xl" />
      </div>
    </div>
  );
}

// ─── Notes Skeleton ─────────────────────────────────────────────────────────

export function NotesSkeleton() {
  return (
    <div className="animate-fade-in flex h-full flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="border-border/30 flex shrink-0 items-center gap-1 overflow-x-auto border-b px-2 py-1.5">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-7 shrink-0 rounded-md" />
        ))}
      </div>
      {/* Editor area */}
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden px-4 py-4 sm:px-6">
        <Skeleton className="h-6 w-[45%] max-w-[280px]" />
        <SkeletonText lines={3} />
        <div className="h-2" />
        <Skeleton className="h-4 w-[30%] max-w-[180px]" />
        <SkeletonText lines={5} />
      </div>
    </div>
  );
}

// ─── Research Session Skeleton ──────────────────────────────────────────────

export function ResearchSessionSkeleton() {
  return (
    <div className="animate-fade-in flex h-full overflow-hidden">
      {/* Left panel */}
      <div className="border-border/30 flex min-w-0 flex-1 flex-col overflow-hidden border-r">
        <div className="border-border/30 flex shrink-0 items-center gap-2 border-b px-3 py-2">
          <Skeleton className="h-7 w-20 rounded-md" />
          <Skeleton className="h-7 w-20 rounded-md" />
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <Skeleton className="h-5 w-[40%] max-w-[200px]" />
          <SkeletonText lines={4} />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      </div>
      {/* Right panel — hidden on narrow viewports */}
      <div className="hidden w-80 shrink-0 flex-col overflow-hidden lg:flex">
        <div className="border-border/30 flex shrink-0 items-center gap-2 border-b px-3 py-2">
          <Skeleton className="h-7 w-16 rounded-md" />
          <Skeleton className="h-7 w-16 rounded-md" />
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <SkeletonText lines={3} />
          <Skeleton className="h-24 rounded-lg" />
          <SkeletonText lines={2} />
        </div>
      </div>
    </div>
  );
}

// ─── Search Skeleton ────────────────────────────────────────────────────────

export function SearchSkeleton() {
  return (
    <div className="animate-fade-in space-y-3 overflow-hidden p-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="border-border/15 flex items-start gap-3 rounded-lg border p-3">
          <Skeleton className="mt-0.5 h-8 w-8 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-[65%] max-w-[300px]" />
            <Skeleton className="h-2.5 w-[45%] max-w-[200px]" />
            <Skeleton className="h-2.5 w-[30%] max-w-[150px]" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Settings Skeleton ──────────────────────────────────────────────────────

export function SettingsSkeleton() {
  return (
    <div className="animate-fade-in flex h-full overflow-hidden">
      {/* Nav — hidden on narrow viewports */}
      <div className="border-border/30 hidden w-48 shrink-0 space-y-1 overflow-y-auto border-r p-3 md:block">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded-md" />
        ))}
      </div>
      {/* Content */}
      <div className="min-w-0 flex-1 space-y-6 overflow-y-auto p-4 sm:p-6">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-9 w-full max-w-md rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Panel Loading Skeleton ─────────────────────────────────────────────────
// Layout-matching skeleton for lazy-loaded panels in PanelZoneContainer.
// The h-[34px] header bar matches the real PanelZoneContainer tab bar height.

export function PanelSkeleton() {
  return (
    <div className="animate-fade-in flex h-full flex-col overflow-hidden">
      {/* Tab bar — h-[34px] matches PanelZoneContainer */}
      <div className="border-border/40 flex h-[34px] shrink-0 items-center gap-2 border-b px-3">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="ml-auto h-3.5 w-3.5" />
      </div>
      {/* Content area */}
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden p-4">
        <Skeleton className="h-5 w-[50%] max-w-[200px]" />
        <SkeletonText lines={3} />
        <Skeleton className="h-24 rounded-lg" />
        <SkeletonText lines={2} />
      </div>
    </div>
  );
}
